import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import classnames from 'classnames';

import { Link } from 'components/core/Link';

import './Pagination.scss';

export function Pagination({ className, pages, selectedPage, pageLimit = 5 }) {
	const {
		prevPage,
		nextPage,
		notAtStartOfPages,
		notAtEndOfPages,
		pagesToShow,
	} = useMemo(() => {
		if (!pages || pages.length < 2) {
			return {};
		}
		const totalPagesLength = pages.length;
		const pageIndex = selectedPage - 1;

		const startPage = Math.max(1, selectedPage - Math.floor(pageLimit / 2));

		const endPage = Math.min(startPage + pageLimit - 1, totalPagesLength);

		const _notAtStartOfPages = startPage !== 1;
		const _notAtEndOfPages = endPage !== totalPagesLength;

		const _pagesToShow = [];

		for (let i = startPage; i <= endPage; i += 1) {
			_pagesToShow.push(i);
		}

		if (_notAtStartOfPages) {
			_pagesToShow.unshift(1);
		}

		if (endPage !== totalPagesLength) {
			_pagesToShow.push(totalPagesLength);
		}

		return {
			prevPage: get(pages, pageIndex - 1, {}),
			nextPage: get(pages, pageIndex + 1, {}),
			notAtStartOfPages: _notAtStartOfPages,
			notAtEndOfPages: _notAtEndOfPages,
			pagesToShow: _pagesToShow,
		};
	}, [selectedPage, pages, pageLimit]);

	if (!pages || pages.length < 2) return null;

	return (
		<ul className={classnames('ss-pagination', className)}>
			<li className="pagination-item">
				<Link
					className={classnames('pagination-link', {
						disabled: selectedPage === 1,
					})}
					to={prevPage.url}
					rel={prevPage.nofollow ? 'nofollow' : null}
					aria-disabled={selectedPage === 1}
				>
					&laquo;
					<span className="visually-hidden">Previous page </span>
				</Link>
			</li>
			{pagesToShow?.map((page, index) => {
				const canShowStartExpander = index === 1 && notAtStartOfPages;
				const canShowEndExpander =
					index === pagesToShow.length - 2 && notAtEndOfPages;

				let paginationItemContent;

				if (canShowStartExpander || canShowEndExpander) {
					paginationItemContent = '...';
				} else {
					paginationItemContent = page;
				}

				return (
					<li
						className="pagination-item"
						key={page}
					>
						<Link
							className={classnames('pagination-link', {
								active: page === selectedPage,
							})}
							to={pages[page - 1].url}
							rel={pages[page - 1].nofollow ? 'nofollow' : null}
							aria-current={
								page === selectedPage ? 'page' : undefined
							}
						>
							<span className="visually-hidden">Page</span>
							{paginationItemContent}
						</Link>
					</li>
				);
			})}
			<li className="pagination-item">
				<Link
					className={classnames('pagination-link', {
						disabled: selectedPage === pages.length,
					})}
					to={nextPage.url}
					rel={nextPage.nofollow ? 'nofollow' : null}
					aria-disabled={selectedPage === pages.length}
				>
					<span className="visually-hidden">Next page </span>&raquo;
				</Link>
			</li>
		</ul>
	);
}

Pagination.propTypes = {
	className: PropTypes.string,
	pages: PropTypes.arrayOf(
		PropTypes.shape({
			url: PropTypes.string,
			label: PropTypes.number,
			nofollow: PropTypes.bool,
		}),
	).isRequired,
	selectedPage: PropTypes.number,
	pageLimit: PropTypes.number,
};

export default Pagination;
